import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withApollo } from 'react-apollo';
import RoundClose from 'react-md-icon/dist/RoundClose';
import { debounce } from 'debounce';

import MainLayout from 'components/templates/main-layout';

import Button from 'components/common/button';
import { Card, CardHeader, CardBody } from 'components/common/card';
import confirmation from 'components/common/confirmation';
import Hr from 'components/common/hr';
import Input from 'components/common/input';
import Loader from 'components/common/loader';
import Typography from 'components/common/typography';
import toast from 'components/common/toast';

import {
  Form,
  LocationList,
  LocationTagContainer,
  LocationTagText,
  LocationIcon,
  Actions,
} from './elements';

import { GET_LOCATIONS, CREATE_STORAGE_REPORT } from './requests';

class InventoryReport extends Component {
  state = {
    locationInput: '',
    label: '',
    locations: [],
    location: null,
    locationsFound: [],
    locationsNotFound: [],
    locationsCount: null,
    loading: false,
    loadingReport: false,
    notFoundMessage: '',
  };

  componentDidMount = async () => {
    try {
      const { errors, data } = await this.getLocations();

      if (errors) {
        toast.error('Algo salió mal, intentalo de nuevo');
        return;
      }

      this.setState({ locationsCount: data.locations.length });
    } catch (error) {
      toast.error('Algo salió mal, intentalo cerrando sesíon e iniciando nuevamente');
    }
  };

  findLocation = debounce(value => {
    let tags = [];

    if (value.includes(', ')) {
      tags = value.split(', ');
    } else if(value.includes(',')) {
      tags = value.split(',');
    } else {
      tags = value.split(' ');
    }

    value.length && this.getLocationByFilter(tags);

    if (!value.length) {
      this.clearInput();
    }
  }, 500);

  clearInput = () => this.setState({ message: '', locationInput: '', notFoundMessage: '', locationsNotFound: [], locationsFound: [] });

  handleLocationInput = e => {
    const {
      target: { value },
    } = e;

    this.findLocation(value);

    this.setState({ locationInput: value });
  };

  handleLabelChange = event => {
    const {
      target: { value },
    } = event;

    this.setState({ label: value });
  }

  removeDuplicates = (array) => array.reduce((prev, current) => {
    if (!prev.some(obj => obj.tag === current.tag)) {
      prev.push(current);
    }

    return prev;
  },[]);

  handleAddLocations = e => {
    const { locationsFound, locations, loading } = this.state;
    e.preventDefault();

    if (!loading) {
      if (locationsFound) {
        this.setState({
          locations: this.removeDuplicates([...locations, ...locationsFound]),
          locationInput: '',
        });
  
        this.clearInput();
      }
    }
  };

  handleRemoveLocation = locationId => {
    const { locations } = this.state;
    this.setState({ locations: locations.filter(location => location.id !== locationId) });
  };

  addAllLocations = async () => {
    this.setState({ loading: true });

    try {
      const { errors, data } = await this.getLocations();

      if (errors) {
        toast.error('Algo salió mal, intentalo de nuevo');
        this.setState({ loading: false });
        return;
      }

      const { locations } = data;

      this.setState({ locations });
    } catch (error) {
      toast.error('Algo salió mal, intentalo cerrando sesíon e iniciando nuevamente');
    } finally {
      this.setState({ loading: false });
      this.clearInput();
    }
  };

  removeAllLocations = () => this.setState({ locations: [] });

  getLocations = () => {
    const { client } = this.props;

    return client.query({
      query: GET_LOCATIONS,
    });
  };

  getLocationByFilter = async tags => {
    const { client } = this.props;

    this.setState({ loading: true });

    try {
      const { data, errors } = await client.query({
        query: GET_LOCATIONS,
        variables: {
          where: {
            tag: tags,
          },
        },
      });

      if (errors) {
        toast.error('Algo salió mal, intentalo de nuevo');
        return;
      }

      const { locations } = data;

      const locationsNotFound = tags.filter(tag => !locations.some(location => location.tag === tag.toUpperCase()));

      let message = `Locaciones encontradas: ${locations.map(location => location.tag)}`;
      let notFoundMessage = `Locaciones NO encontradas: ${locationsNotFound.map(location => location.toUpperCase())}`;

      if (!locations.length) {
        message = '';
      }

      if (!locationsNotFound.length) {
        notFoundMessage = '';
      }

      this.setState({
        locationsFound: locations,
        locationsNotFound,
        message,
        notFoundMessage,
      });
    } catch (error) {
      toast.error('Algo salió mal, intentalo cerrando sesíon e iniciando nuevamente');
    } finally {
      this.setState({ loading: false });
    }
  };

  createInventoryReport = async () => {
    const { client } = this.props;
    const { locations, label } = this.state;

    if (await confirmation('¿Estás seguro?')) {
      this.setState({ loadingReport: true });

      try {
        const { errors } = await client.mutate({
          mutation: CREATE_STORAGE_REPORT,
          variables: { data: { locations, label } },
        });

        if (errors) {
          let message;
          const [error] = errors;
          if (error.includes('Unable to create a new report')) {
            message = 'No es posible crear un reporte, ya hay un reporte activo';
          } else {
            message = 'Algo salió mal, intentalo de nuevo';
          }
          toast.error(message);
          this.setState({ loadingReport: false });
          return;
        }

        toast.success('Reporte de inventario creado éxitosamente');
        this.setState({ locations: [], label: '' });
      } catch (error) {
        toast.error('Algo salió mal, intentalo cerrando sesíon e iniciando nuevamente');
      } finally {
        this.setState({ loadingReport: false });
      }
    }
  };

  render() {
    const {
      locationInput,
      locationsCount,
      locations,
      locationsFound,
      loading,
      loadingReport,
      message,
      notFoundMessage,
      label,
    } = this.state;

    const allLocationsAdded = locationsCount === locations.length;

    return (
      <MainLayout flexColumn>
        <Typography sm>
          Elige las ubicaciones que se van a habilitar en el sistema. Dichas ubicaciones estarán
          disponibles para un conteo de sus productos. El sistema no permitirá realizar conteos en
          ubicaciones que no estén habilitadas.
        </Typography>
        <Hr />
        <Card marginT="30">
          <CardBody>
            <Input
              input
              primary
              label="Escribe la etiqueta del inventario"
              onChange={this.handleLabelChange}
              value={label}
              type="text"
            />
          </CardBody>
        </Card>
        <Card marginT="30">
          <CardBody>
            <Form onSubmit={this.handleAddLocations}>
              <Actions>
                <div className="input-container">
                  <Input
                    input
                    primary
                    label="Escribe el nombre de una o varias ubicaciones existentes en el inventario (Separadas por , o un espacio en blanco)"
                    onChange={this.handleLocationInput}
                    value={locationInput}
                    required
                    placeholder="Ejemplo: A-1, A-2"
                    type="text"
                    errorMessage={notFoundMessage}
                    message={message}
                    disabled={allLocationsAdded}
                  />
                </div>                  
                <Button
                  xs
                  not={!locationsFound.length}
                  disabled={!locationsFound.length}
                >
                  {
                    loading 
                    ? <Loader className="loader" inverse size="15" />
                    : 'Habilitar ubicaciones'
                  }
                </Button>
              </Actions>
              <Button
                sm
                marginT="40"
                inverse
                type="button"
                alignSelf="flex-end"
                disabled={loading}
                onClick={allLocationsAdded ? this.removeAllLocations : this.addAllLocations}
              >
                {allLocationsAdded
                  ? 'DESHABILITAR todas las ubicaciones'
                  : 'Habiiltar todas las ubicaciones'}
              </Button>
            </Form>
          </CardBody>
        </Card>
        <Card marginT="40">
          <CardHeader subtitle="Aquí aparecerán las ubicaciones que añadas" />
          <LocationList center={allLocationsAdded}>
            {allLocationsAdded ? (
              <Typography md align="center">
                Se han añadido todas las ubicaciones
              </Typography>
            ) : (
              locations.map(location => (
                <LocationTagContainer key={location.id}>
                  <LocationTagText xs>{location.tag}</LocationTagText>
                  <LocationIcon onClick={() => this.handleRemoveLocation(location.id)}>
                    <RoundClose />
                  </LocationIcon>
                </LocationTagContainer>
              ))
            )}
          </LocationList>
        </Card>
        {loadingReport ? (
          <Loader alignSelf="flex-end" marginT="40" />
        ) : (
          <Button
            alignSelf="flex-end"
            type="button"
            sm
            marginT="40"
            not={!locations.length && !label.length}
            disabled={!locations.length && !label.length}
            onClick={this.createInventoryReport}
          >
            Crear inventario
          </Button>
        )}
      </MainLayout>
    );
  }
}

InventoryReport.propTypes = {
  client: PropTypes.shape().isRequired,
};

export default withApollo(InventoryReport);
